import type { Integration, PipelinesConnection } from '@/services/db/integration/entity';
import { ConnectionType, DataFlow, IntegrationType } from './catalog/enums';
import { getPipelinesIntegrationImageSrcOrIcon } from './catalog/images';

/**
 * Pure derived getters over the raw integration + pipelines-connection wire
 * records (from `services/db/integration`), computing the view values the
 * directory/overview pages read. Detail-page-only getters (`ctaHref`,
 * `parsedUseCases`, `path`, `public`) are intentionally out of scope.
 */

/** Backend `type` strings that fall in the `Direct` connection group. */
const DIRECT_TYPES: readonly string[] = [
  IntegrationType.experiment,
  IntegrationType.native,
  IntegrationType.nativeDataOut,
  IntegrationType.connectPartner,
  IntegrationType.existing,
  IntegrationType.partnerBuilt,
  IntegrationType.pipelinesSource,
  IntegrationType.pipelinesDestination,
];

/** Backend `type` strings whose `publicType` collapses to `Existing`. */
const EXISTING_PUBLIC_TYPES: readonly string[] = [
  IntegrationType.experiment,
  IntegrationType.native,
  IntegrationType.nativeDataOut,
  IntegrationType.zapier,
  IntegrationType.partnerBuilt,
  IntegrationType.pipelinesSource,
  IntegrationType.pipelinesDestination,
];

function isPipelinesType(type: string): boolean {
  return type === IntegrationType.pipelinesSource || type === IntegrationType.pipelinesDestination;
}

/** `connectionType` — explicit field, else a type-based fallback. */
export function integrationConnectionType(integration: Pick<Integration, 'type' | 'connection_type'>): ConnectionType {
  if (integration.connection_type != null && integration.connection_type.length > 0) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return integration.connection_type as ConnectionType;
  }
  if (DIRECT_TYPES.includes(integration.type)) {
    return ConnectionType.direct;
  }
  return ConnectionType.zapier;
}

/** `icon` — Pluma icon name, only for pipelines Source/Destination. */
export function integrationIcon(integration: Pick<Integration, 'id' | 'type'>): string | undefined {
  if (isPipelinesType(integration.type)) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return getPipelinesIntegrationImageSrcOrIcon(integration.id, integration.type as IntegrationType).icon;
  }
  return undefined;
}

/**
 * `imageSrc` — priority chain: pipelines image override →
 * `image_url` → Brandfetch lettermark from `homepage` → empty string. The
 * Brandfetch URL carries no `?c=<client-id>` query (Hydra has no brandfetch
 * token surface; see PR notes).
 */
export function integrationImageSrc(integration: Pick<Integration, 'id' | 'type' | 'image_url' | 'homepage'>): string {
  if (isPipelinesType(integration.type)) {
    const { imageSrc: imageURLOverride } = getPipelinesIntegrationImageSrcOrIcon(
      integration.id,
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      integration.type as IntegrationType,
    );
    if (imageURLOverride != null) {
      return imageURLOverride;
    }
  }
  if (integration.image_url != null && integration.image_url.length > 0) {
    return integration.image_url;
  }
  if (integration.homepage != null && integration.homepage.length > 0) {
    const host = integration.homepage.replace(/https?:\/\/(www\.)?/, '').replace(/\/$/, '');
    return `https://cdn.brandfetch.io/${host}/lettermark`;
  }
  return '';
}

/** `dataFlow` — the data-flow discriminant (`''` when unknown). */
export function integrationDataFlow(integration: Pick<Integration, 'type' | 'direction'>): DataFlow | '' {
  if (integration.type === IntegrationType.pipelinesSource) {
    return DataFlow.in;
  }
  if (integration.type === IntegrationType.nativeDataOut || integration.type === IntegrationType.pipelinesDestination) {
    return DataFlow.out;
  }
  if (integration.direction?.toLowerCase() === 'data out') {
    return DataFlow.out;
  }
  if (integration.direction?.toLowerCase() === 'data in') {
    return DataFlow.in;
  }
  return '';
}

/** `publicType` — collapses the internal types to a public taxonomy. */
export function integrationPublicType(integration: Pick<Integration, 'type'>): IntegrationType {
  if (EXISTING_PUBLIC_TYPES.includes(integration.type)) {
    return IntegrationType.existing;
  }
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return integration.type as IntegrationType;
}

/**
 * `enabled` — only meaningful for `NativeDataOut` integrations,
 * where enablement is proven by a successful `data_out/:identifier` fetch. The
 * caller supplies the set of enabled NativeDataOut ids.
 */
export function integrationEnabled(
  integration: Pick<Integration, 'id' | 'type'>,
  enabledDataOutIds: ReadonlySet<string>,
): boolean {
  return integration.type === IntegrationType.nativeDataOut && enabledDataOutIds.has(integration.id);
}

/* ------------------------------------------------------------------------- */
/* Pipelines connection derived getters                                       */
/* ------------------------------------------------------------------------- */

/** The compound id that joins a connection to its catalog integration record. */
export function pipelinesConnectionIntegrationId(connection: Pick<PipelinesConnection, 'direction' | 'type'>): string {
  return `pipelines_${connection.direction}_${connection.type}`;
}

/** Strip the `pipelines_<direction>_` prefix to recover the backend's native slug. */
export function pipelinesConnectionOriginalId(connection: Pick<PipelinesConnection, 'id' | 'direction'>): string {
  return connection.id.replace(`pipelines_${connection.direction}_`, '');
}

/** `dataFlow` — `Source` → in, `Destination` → out, else null. */
export function pipelinesConnectionDataFlow(connection: Pick<PipelinesConnection, 'direction'>): DataFlow | null {
  if (connection.direction === IntegrationType.pipelinesSource) {
    return DataFlow.in;
  }
  if (connection.direction === IntegrationType.pipelinesDestination) {
    return DataFlow.out;
  }
  return null;
}

/**
 * `imageSrc` — `customerio` uses the fixed logomark,
 * everything else delegates to the joined catalog integration's imageSrc. The
 * caller resolves the joined integration via `pipelinesConnectionIntegrationId`.
 */
export function pipelinesConnectionImageSrc(
  connection: Pick<PipelinesConnection, 'type'>,
  integrationModel?: Pick<Integration, 'id' | 'type' | 'image_url' | 'homepage'>,
): string | undefined {
  if (connection.type === 'customerio') {
    return getPipelinesIntegrationImageSrcOrIcon('customerio').imageSrc;
  }
  return integrationModel != null ? integrationImageSrc(integrationModel) : undefined;
}

/** `icon` — delegates to the joined catalog integration. */
export function pipelinesConnectionIcon(integrationModel?: Pick<Integration, 'id' | 'type'>): string | undefined {
  return integrationModel != null ? integrationIcon(integrationModel) : undefined;
}

/** `connectionType` — joined integration's, defaulting to Direct. */
export function pipelinesConnectionType(
  integrationModel?: Pick<Integration, 'type' | 'connection_type'>,
): ConnectionType {
  return integrationModel != null ? integrationConnectionType(integrationModel) : ConnectionType.direct;
}
